import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  tokens,
  Badge,
  Spinner,
  Text,
  Toast,
  ToastTitle,
  ToastBody,
  Toaster,
  ToolbarButton,
  Tooltip,
  useId,
  useToastController,
} from '@fluentui/react-components';
import {
  Eye20Regular,
  EyeOff20Regular,
  PanelLeft24Regular,
} from '@fluentui/react-icons';
import Header from '../components/Header';
import DataTable, { ColumnConfig } from '../components/DataTable';
import ProductGroupFiltersSidebar from '../components/ProductGroupFiltersSidebar';
import { useProductGroupFilters } from '../hooks/useProductGroupFilters';
import { apiFetch } from '../lib/apiFetch';
import { useSelection } from '../contexts/SelectionContext';

interface ProductGroup {
  id: string;
  name: string;
  productType: 'rental' | 'service' | 'sales_item';
  productTypeLabel: string;
  pricingCents: number;
  showInStore: boolean;
  sku: string;
  photoUrl?: string;
}

const useStyles = makeStyles({
  workspaceCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    flexGrow: 1,
    // Floor to the viewport so flexGrow can't collapse the card to content
    // height when the nested flex height chain fails to resolve (gray gap
    // below the work surface on scroll). flexShrink:0 prevents re-squeeze.
    minHeight: 'calc(100vh - 64px)',
    flexShrink: 0,
    ...shorthands.margin('8px'),
    ...shorthands.overflow('hidden'),
    // Mobile: full-bleed work surface — drop the margin gutter and rounded
    // corners so the content uses every pixel of width.
    '@media (max-width: 768px)': {
      ...shorthands.margin('0'),
      ...shorthands.borderRadius('0'),
    },
  },
  body: {
    display: 'flex',
    alignItems: 'stretch',
    flexGrow: 1,
    minHeight: 0,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
    minHeight: 0,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.padding('40px'),
    width: '100%',
  },
  productCell: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
  productImage: {
    width: '40px',
    height: '40px',
    ...shorthands.borderRadius('4px'),
    objectFit: 'cover',
    flexShrink: 0,
  },
  productIcon: {
    width: '40px',
    height: '40px',
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorBrandBackground,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontWeight: 'bold',
    fontSize: '16px',
    flexShrink: 0,
  },
});

// Keys match Booqable's product_type values: 'rental' | 'sales_item' | 'service'.
const PRODUCT_TYPE_LABELS: Record<string, string> = {
  rental: 'Rental',
  service: 'Service',
  sales_item: 'Sale Item',
};

const formatPrice = (cents: number) =>
  cents > 0 ? `$${(cents / 100).toFixed(2)}` : '—';

export default function ProductGroups() {
  const styles = useStyles();
  const navigate = useNavigate();
  const [groups, setGroups] = useState<ProductGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [syncing, setSyncing] = useState(false);
  const toasterId = useId('product-groups-toaster');
  const { dispatchToast } = useToastController(toasterId);
  const { setSelectedCount, setOnClearSelection } = useSelection();
  const filters = useProductGroupFilters();
  const filterPanelCollapsed = filters.state.sidebarCollapsed;
  const toggleFilterPanel = () =>
    filters.setField('sidebarCollapsed', !filters.state.sidebarCollapsed);

  // Mirror selection count into SelectionContext so the global header
  // can render the "N selected" badge — same wiring as Orders/Customers.
  useEffect(() => {
    setSelectedCount(selectedIds.length);
  }, [selectedIds, setSelectedCount]);

  useEffect(() => {
    setOnClearSelection(() => () => setSelectedIds([]));
  }, [setOnClearSelection]);

  useEffect(() => {
    return () => {
      setSelectedCount(0);
    };
  }, [setSelectedCount]);

  // Wired to the toolbar's "Sync Booqable" button — populates TWM's
  // product_groups + products mirror. Required for features that read
  // TWM-local product metadata.
  useEffect(() => {
    const handleSync = async () => {
      if (syncing) return;
      setSyncing(true);
      dispatchToast(
        <Toast><ToastTitle>Syncing with Booqable…</ToastTitle></Toast>,
        { intent: 'info' }
      );
      try {
        const res = await apiFetch('/products/sync', { method: 'POST' });
        const data = await res.json();
        if (data.success) {
          const g = data.product_groups || {};
          const p = data.products || {};
          dispatchToast(
            <Toast>
              <ToastTitle>Sync complete</ToastTitle>
              <ToastBody>
                Groups: {g.created} created, {g.updated} updated · Products: {p.created} created, {p.updated} updated
              </ToastBody>
            </Toast>,
            { intent: 'success' }
          );
        } else {
          dispatchToast(
            <Toast><ToastTitle>Sync failed</ToastTitle><ToastBody>{data.error}</ToastBody></Toast>,
            { intent: 'error' }
          );
        }
      } catch (e) {
        dispatchToast(
          <Toast><ToastTitle>Sync failed</ToastTitle><ToastBody>Network error</ToastBody></Toast>,
          { intent: 'error' }
        );
      } finally {
        setSyncing(false);
      }
    };
    window.addEventListener('syncBooqableProducts', handleSync);
    return () => window.removeEventListener('syncBooqableProducts', handleSync);
  }, [syncing, dispatchToast]);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setLoading(true);
        // include_hidden=true so back-office services / sale-items show up.
        const res = await apiFetch(`/booqable/product-groups?include_hidden=true`);
        const data = await res.json();
        const mapped: ProductGroup[] = (data.product_groups || []).map((item: any) => ({
          id: item.id,
          name: item.name,
          productType: item.product_type,
          productTypeLabel: PRODUCT_TYPE_LABELS[item.product_type] ?? item.product_type,
          pricingCents: item.base_price_in_cents || 0,
          showInStore: !!item.show_in_store,
          sku: item.sku || 'N/A',
          photoUrl: item.photo_url,
        }));
        setGroups(mapped);
        setError(null);
      } catch (err) {
        console.error('Error fetching product groups:', err);
        setError('Failed to load product groups');
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, []);

  const columns: ColumnConfig<ProductGroup>[] = [
    {
      columnKey: 'checkbox',
      label: '',
      width: '50px',
    },
    {
      columnKey: 'name',
      label: 'Name',
      sortable: true,
      render: (g) => (
        <div className={styles.productCell}>
          {g.photoUrl ? (
            <img src={g.photoUrl} alt={g.name} className={styles.productImage} />
          ) : (
            <div className={styles.productIcon}>{g.name.charAt(0)}</div>
          )}
          <span>{g.name}</span>
        </div>
      ),
    },
    {
      columnKey: 'productTypeLabel',
      label: 'Product Type',
      sortable: true,
      groupable: true,
    },
    {
      columnKey: 'pricingCents',
      label: 'Pricing',
      sortable: true,
      render: (g) => formatPrice(g.pricingCents),
    },
    {
      columnKey: 'showInStore',
      label: 'Online Store Visibility',
      sortable: true,
      groupable: true,
      render: (g) => g.showInStore ? (
        <Badge appearance="tint" color="success" icon={<Eye20Regular />}>Visible</Badge>
      ) : (
        <Badge appearance="outline" icon={<EyeOff20Regular />}>Hidden</Badge>
      ),
    },
    {
      columnKey: 'sku',
      label: 'SKU',
      sortable: true,
    },
  ];

  const filteredGroups = useMemo(
    () => filters.applyFilters(groups),
    [filters, groups],
  );

  return (
    <div className={styles.workspaceCard}>
      <Toaster toasterId={toasterId} />
      <Header
        inline
        leftActions={
          <Tooltip
            content={filterPanelCollapsed ? 'Show filters' : 'Hide filters'}
            relationship="label"
          >
            <ToolbarButton
              icon={<PanelLeft24Regular />}
              onClick={toggleFilterPanel}
              aria-label={filterPanelCollapsed ? 'Show filters' : 'Hide filters'}
            />
          </Tooltip>
        }
        customTitle={
          <span style={{
            fontSize: tokens.fontSizeBase500,
            fontWeight: tokens.fontWeightSemibold,
            lineHeight: tokens.lineHeightBase300,
            color: tokens.colorNeutralForeground1,
          }}>
            Inventory
          </span>
        }
      />
      <div className={styles.body}>
        <ProductGroupFiltersSidebar
          filters={filters}
          groups={groups}
          collapsed={filterPanelCollapsed}
        />
        <div className={styles.content}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <Spinner label="Loading product groups..." />
            </div>
          ) : error ? (
            <div className={styles.loadingContainer}>
              <Text>{error}</Text>
            </div>
          ) : (
            <DataTable
              data={filteredGroups}
              columns={columns}
              keyField="id"
              hideableColumns
              columnVisibilityStorageKey="product_groups"
              itemsPerPage={50}
              showCheckboxes={true}
              onSelectionChange={setSelectedIds}
              primaryColumnKey="name"
              onRowClick={(g) => navigate(`/product_groups/${g.id}/inventory`)}
              disableWrapper={true}
            />
          )}
        </div>
      </div>
    </div>
  );
}
